import React, { useEffect, useMemo, useState } from 'react';
import {
  List, ListItem, Card, Typography, Box, Divider, Grid, Checkbox, IconButton,
  TextField, InputAdornment, Button, Chip
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  AccessTimeOutlined, CloseOutlined, DeleteOutline, ExpandMoreOutlined, LocalShippingOutlined,
  RoomOutlined, StraightenOutlined, ViewInArOutlined
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useDroppable } from '@dnd-kit/core';
import TruckIcon from '@mui/icons-material/LocalShippingOutlined';
import CarIcon from '@mui/icons-material/DirectionsCar';
import VanIcon from '@mui/icons-material/AirportShuttle';
import { i18next } from '@/config/i18n';
import { alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getLoadClassData } from '@/common/common';
import Fuse from 'fuse.js';
import Icon from '@/components/Common/Icons/Icon';
import * as Icons from '@/components/Common/Icons/Icons';

export interface RouteItem { id: string; color: string; icon: 'truck' | 'car' | 'van'; }

interface Props {
  routes: RouteItem[];
  routeDetails: Record<string, any>;
  vehicleDetails: Record<string, any>;
  onSelect?: (route: RouteItem) => void;
  selectedId?: string | null;
  tourColorsById: Record<string, string>;
  editMode: boolean;
  onSelectionChange?: (ids: string[]) => void;
  hidden?: boolean;
  highlightedId?: string | null;
  pendingAssignments?: Record<string, string[]>;
  onRemoveShipments?: (routeIds: string[]) => void;
}

const useStyles = makeStyles((theme: any) => ({
  wrapperCard: { display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 0 },
  headerBox: { padding: '8px 10px' },
  innerSearch: {
    padding: '11px',
  },
  headerRow: {
    padding: '8px 10px',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  totalvehicle: { fontSize: '16px' },
  list: { flex: 1, overflowY: 'auto', padding: '16px 10px 0 10px', backgroundColor: theme.palette.background.paper },
  card: { marginBottom: theme.spacing(2), borderRadius: theme.shape.borderRadius, overflow: 'hidden' },
  textBox: { marginLeft: theme.spacing(1), fontSize: '13px' },
  detailsBox: { padding: theme.spacing(0.5, 1, 0), backgroundColor: theme.palette.background.default },
  touridno: { fontSize: '14px', padding: '10px', marginBottom: '10px' },
  TourMapDetail: { fontSize: '21px' },
  detailinfo: { fontSize: '13px', fontWeight: '500' },
  detailSec: { padding: '0px 10px' },
  multiTourBar: {
    width: 12, height: '100%', position: 'absolute', top: 0, left: 0,
    borderTopLeftRadius: 4, borderBottomLeftRadius: 4, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  halfColor: { flex: 1 },
  itemRow: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    alignItems: 'stretch',
  },
  itemRowEdit: {
    gridTemplateColumns: '36px 1fr',
  },
  checkboxCell: {
    display: 'grid',
    placeItems: 'center',
    alignSelf: 'stretch',
    height: 43,
  },
  selectionBar: {
    top: 97,
    position: 'sticky',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    background: '#fff',
    borderBottom: '1px solid rgba(0,0,0,0.12)',
  },
  leftBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  deleteIcon: {
    color: "#808080",
    fontSize: '22px'
  },
  deleteText: {
    fontSize: '15px'
  }
}));

const iconMap = { truck: <TruckIcon fontSize="small" style={{ fontSize: 25 }} />, car: <CarIcon fontSize="small" />, van: <VanIcon fontSize="small" /> };

const DroppableVehicle: React.FC<{
  vehicleId: string;
  enable: boolean;
  children: (ref: (node: HTMLElement | null) => void, isOver: boolean) => React.ReactNode;
}> = ({ vehicleId, enable, children }) => {
  if (!enable) return <>{children(() => { }, false)}</>;
  const { isOver, setNodeRef } = useDroppable({
    id: vehicleId,
    data: { type: 'vehicle', vehicleId },
    disabled: !enable,
  });

  return <>{children(setNodeRef, !!isOver && enable)}</>;
};

// small debounce hook
function useDebounced<T>(value: T, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
}

const RoutesList: React.FC<Props> = ({
  routes, routeDetails, vehicleDetails, onSelect, selectedId, tourColorsById,
  editMode, onSelectionChange, hidden = false, highlightedId = null,
  pendingAssignments = {}, onRemoveShipments
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [emptyOnTop, setEmptyOnTop] = useState<boolean>(true);

  // SEARCH state
  const [q, setQ] = useState('');
  const qDeb = useDebounced(q, 250);

  // helper
  const norm = (s?: string) => (s ?? '').trim().toLowerCase();

  const getPendingCount = (
    map: Record<string, any[] | undefined>,
    routeId: string,
    vehicleName?: string
  ) => {
    if (!map) return 0;
    const k1 = norm(vehicleName);
    const k2 = norm(routeId);
    return (map[vehicleName ?? '']?.length)
      ?? (map[routeId]?.length)
      ?? Object.entries(map).find(([k]) => norm(k) === k1)?.[1]?.length
      ?? Object.entries(map).find(([k]) => norm(k) === k2)?.[1]?.length
      ?? 0;
  };

  useEffect(() => { if (hidden) setExpandedId(null); }, [hidden]);
  if (hidden) return null;

  useEffect(() => { if (!editMode && selectedRoutes.length) setSelectedRoutes([]); }, [editMode, selectedRoutes.length]);
  useEffect(() => { onSelectionChange?.(selectedRoutes); }, [selectedRoutes, onSelectionChange]);

  useEffect(() => {
    const valid = new Set(routes.map(r => r.id));
    setSelectedRoutes(prev => {
      const next = prev.filter(id => valid.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [routes]);

  const handleCheckboxChange = (routeId: string) => {
    setSelectedRoutes((prev) => prev.includes(routeId) ? prev.filter((id) => id !== routeId) : [...prev, routeId]);
  };

  const toggleExpand = (id: string, e: React.MouseEvent) => { e.stopPropagation(); setExpandedId((p) => (p === id ? null : id)); };

  const formatTime = (totalMinutes: number): string => { if (!totalMinutes) return '--'; const h = Math.floor(totalMinutes / 60); const m = totalMinutes % 60; return `${h} h ${m} min`; };
  const convertMeterToKm = (m: number): number => Math.round(m / 1000);

  // Build searchable docs aligned by index to `routes`
  const docs = useMemo(() => {
    return routes.map(r => {
      const detailsArray = routeDetails[r.id] || [];
      const vehicleName = Array.isArray(detailsArray) ? detailsArray[0]?.vehicle_name : detailsArray?.vehicle_name;
      const tourIds = Array.isArray(detailsArray) ? detailsArray.map((d: any) => d?.id).filter(Boolean).join(' ') : (detailsArray?.id ?? '');
      return {
        routeId: String(r.id),
        vehicleName: String(vehicleName ?? ''),
        tourIds: String(tourIds ?? '')
      };
    });
  }, [routes, routeDetails]);

  const fuse = useMemo(() => new Fuse(docs, {
    keys: ['routeId', 'vehicleName', 'tourIds'],
    threshold: 0.33,
    ignoreLocation: true,
  }), [docs]);

  const visibleRoutes = useMemo(() => {
    const term = qDeb.trim();
    const idxSet = term ? new Set(fuse.search(term).map(r => r.refIndex)) : null;
    const base = term ? routes.filter((_, i) => idxSet!.has(i)) : routes;
    if (!emptyOnTop) return base;
    return base.slice().sort((a, b) => {
      const aLen = Array.isArray(routeDetails[a.id]) ? routeDetails[a.id].length : 0;
      const bLen = Array.isArray(routeDetails[b.id]) ? routeDetails[b.id].length : 0;
      const aEmpty = aLen === 0 ? 1 : 0;
      const bEmpty = bLen === 0 ? 1 : 0;
      return bEmpty - aEmpty;
    });
  }, [routes, fuse, qDeb, emptyOnTop, routeDetails]);

  return (
    <Card variant="outlined" className={classes.wrapperCard}>
      {/* Header + Search */}
      <Box className={classes.headerRow}>
        <Typography className={classes.totalvehicle} variant="subtitle2" color="textSecondary">
          {i18next.t('common:vehicleEditor.vehiclesTitle')} ({visibleRoutes.length}/{routes.length})
        </Typography>
      </Box>
      <Box className={classes.innerSearch}>
        <TextField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={i18next.t('common:placeholders.searchRoutesVehicleTour')}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: q ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQ('')} aria-label="clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>
      <Divider />

      {editMode && selectedRoutes.length > 0 && (
        <Box className={classes.selectionBar}>
          <Box className={classes.leftBox}>
            <IconButton size="small" onClick={() => setSelectedRoutes([])}>
              <CloseOutlined fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" color="textSecondary">
              {selectedRoutes.length} {i18next.t('common:RouteList.Selected')}
            </Typography>
          </Box>
          <Box
            onClick={() => onRemoveShipments?.(selectedRoutes)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: '#808080',
              fontWeight: 600,
              '&:hover': { color: '#000' },
            }}
          >
            <DeleteOutline className={classes.deleteIcon} />
            <Typography variant="subtitle2" color="textSecondary" className={classes.deleteText}>
              {i18next.t('common:DroppedShipment.removeShipmentTitle')}
            </Typography>
          </Box>
        </Box>
      )}

      <List className={classes.list}>
        {visibleRoutes.map((route) => {
          const isSelected = route.id === selectedId;
          const isExpanded = route.id === expandedId;
          const isHighlighted = highlightedId === route.id;
          const detailsArray = routeDetails[route.id] || [];
          const isEmptyVehicle = Array.isArray(detailsArray) ? detailsArray.length === 0 : true;
          const vehicleName =
            Array.isArray(detailsArray) ? detailsArray[0]?.vehicle_name : detailsArray?.vehicle_name;
          // @ts-ignore
          const filteredVehicle = vehicleDetails.find((v) => v.name === vehicleName);
          const load_class = getLoadClassData(filteredVehicle);
          const pendingForVehicle = getPendingCount(pendingAssignments ?? {}, route.id, vehicleName);
          const isTrulyEmpty =
            (Array.isArray(detailsArray) ? detailsArray.length === 0 : true) &&
            pendingForVehicle === 0;

          const hasAdr = (Array.isArray(detailsArray) && detailsArray.some((tour: any) =>
            Array.isArray(tour.shipments) && tour.shipments.some((s: any) => (s.adr_points || 0) > 0)
          )) || (Array.isArray(pendingAssignments[vehicleName]) && pendingAssignments[vehicleName].some((s: any) => (s.adr_points || 0) > 0));

          return (
            <DroppableVehicle key={route.id} vehicleId={route.id} enable={editMode}>
              {(setNodeRef, isOver) => (
                <ListItem
                  ref={setNodeRef}
                  disableGutters
                  disablePadding
                  className={`${classes.itemRow} ${editMode ? classes.itemRowEdit : ''}`}
                >
                  {editMode && (
                    <Box className={classes.checkboxCell} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedRoutes.includes(route.id)}
                        onChange={(e) => { e.stopPropagation(); handleCheckboxChange(route.id); }}
                        size="small"
                        sx={{ p: 0 }}
                      />
                    </Box>
                  )}

                  <Card
                    variant="outlined"
                    className={classes.card}
                    style={{
                      paddingLeft: 12,
                      position: 'relative',
                      outline: (isHighlighted || isOver) ? `2px solid ${alpha(route.color, 0.8)}` : 'none',
                      boxShadow: (isHighlighted || isOver) ? `0 0 0 2px ${alpha(route.color, 0.25)}` : undefined,
                      backgroundColor: isHighlighted
                        ? alpha(route.color, 0.18)
                        : (isSelected ? alpha(route.color, 0.2) : (isTrulyEmpty ? alpha('#9e9e9e', 0.06) : undefined)),
                      transition: 'outline 80ms ease, box-shadow 80ms ease',
                    }}
                  >
                    {/* Color bar */}
                    {Array.isArray(detailsArray) && detailsArray.length > 1 ? (
                      <Box className={classes.multiTourBar}>
                        {detailsArray.map((tour: any) => (
                          <Box
                            key={tour.id}
                            className={classes.halfColor}
                            style={{ backgroundColor: tourColorsById[tour.id] || route.color, flex: 1 }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Box
                        className={classes.multiTourBar}
                        style={{ backgroundColor: tourColorsById[detailsArray[0]?.id] || route.color }}
                      />
                    )}

                    {/* Row */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 13px',
                        height: '43px',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer' }}
                        onClick={() => {
                          if (vehicleName?.trim()) {
                            const encodedName = encodeURIComponent(vehicleName.trim());
                            navigate(`/routes/${encodedName}`, { state: { routeColor: route.color } });
                          }
                        }}
                      >
                        {iconMap[route.icon]}
                        <Typography variant="subtitle1" className={classes.textBox}>
                          {route.id}{' '}

                          {isTrulyEmpty && <Chip size="small" label={i18next.t('common:labels.empty')} sx={{ ml: 1 }} />}
                          {Array.isArray(detailsArray) && detailsArray.length > 1 && (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ fontWeight: 400, color: '#000', fontSize: '13px', ml: 1 }}
                            >
                              ({detailsArray.length})
                            </Typography>
                          )}
                          {pendingForVehicle > 0 && (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ ml: 1, fontWeight: 600, color: 'success.main' }}
                            >
                              (+{pendingForVehicle})
                            </Typography>
                          )}
                        </Typography>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        onClick={(e) => toggleExpand(route.id, e)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {hasAdr && (
                          <Icon
                            svg={Icons.adr}
                            viewBox="0 0 103.5 103.425"
                            sx={{
                              fontSize: '24px',
                            }}
                          />
                        )}
                        <ExpandMoreOutlined
                          style={{
                            marginLeft: 4,
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </Box>
                    </Box>

                    <Divider />

                    {isExpanded && (
                      <Box className={classes.detailsBox}>
                        {Array.isArray(detailsArray) && detailsArray.length > 0 ? (
                          /* ===== Existing planned tours ===== */
                          detailsArray.map((detail: any) => {
                            const summary = detail.summary || {};
                            const tourColor = tourColorsById[detail.id] ?? route.color;
                            let tourAdrPoints = 0;
                            if (Array.isArray(detail.shipments)) {
                              detail.shipments.forEach((s: any) => {
                                tourAdrPoints += (s.adr_points || 0);
                              });
                            }
                            return (
                              <Box key={detail.id} mb={2}>
                                <Grid>
                                  <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    className={classes.touridno}
                                    style={{
                                      backgroundColor:
                                        detailsArray.length === 1
                                          ? alpha(tourColorsById[detail.id] || route.color, 0.15)
                                          : alpha(tourColor, 0.1),
                                    }}
                                  >
                                    <Box component="span" style={{ fontWeight: 700, fontSize: '15px' }}>
                                      {i18next.t('common:RouteList.tour')}
                                    </Box>{' '}
                                    <Box component="span" style={{ fontWeight: 400, fontSize: '13px' }}>
                                      {detail.id}
                                    </Box>
                                  </Typography>
                                </Grid>

                                <Grid container direction="column" spacing={1} style={{ padding: '0px 4px' }}>
                                  <Grid item>
                                    <Grid container alignItems="center" spacing={1}>
                                      <Grid item>
                                        <StraightenOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                      </Grid>
                                      <Grid className={classes.detailSec}>
                                        <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                          {convertMeterToKm(summary.total_distance_m ?? 0)} km
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Grid container alignItems="center" spacing={0.5}>
                                      <Grid item>
                                        <AccessTimeOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                      </Grid>
                                      <Grid className={classes.detailSec}>
                                        <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                          {formatTime(summary.total_working_time_min ?? 0)}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Grid container alignItems="center" spacing={1}>
                                      <Grid item>
                                        <RoomOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                      </Grid>
                                      <Grid className={classes.detailSec}>
                                        <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                          {summary.number_of_customer_stops ?? '--'} {i18next.t('common:VehicleRouteList.stop')}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Grid container alignItems="center" spacing={1}>
                                      <Grid item>
                                        <ViewInArOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                      </Grid>
                                      <Grid className={classes.detailSec}>
                                        <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                          {summary.number_of_planned_shipments ?? '--'} {i18next.t('common:VehicleRouteList.shipments')}
                                          {pendingForVehicle > 0 && (
                                            <Typography
                                              component="span"
                                              variant="body2"
                                              sx={{ ml: 0.75, fontWeight: 600, color: 'success.main' }}
                                            >
                                              (+{pendingForVehicle})
                                            </Typography>
                                          )}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Grid container alignItems="center" spacing={1}>
                                      <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                        <Icon
                                          svg={Icons.adrMono}
                                          viewBox="0 0 103.5 103.425"
                                          sx={{
                                            fontSize: '20px',
                                            color: '#a7a7a7',
                                            marginRight: '2px',
                                            stroke: 'currentColor',
                                            strokeWidth: 4,
                                            transform: 'translateY(-4px)'
                                          }}
                                        />
                                      </Grid>
                                      <Grid className={classes.detailSec}>
                                        <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                          {tourAdrPoints > 0 ? `${tourAdrPoints} ADR` : '--'}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Grid container alignItems="center" spacing={1}>
                                      <Grid item>
                                        <LocalShippingOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                      </Grid>
                                      <Grid className={classes.detailSec}>
                                        <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                          {load_class ?? '--'}{' '}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Box>
                            );
                          })
                        ) : pendingForVehicle > 0 ? (
                          /* ===== Pending tour preview for empty vehicle ===== */
                          <Box mb={2}>
                            <Grid>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                className={classes.touridno}
                                style={{ backgroundColor: alpha(route.color, 0.1) }}
                              >
                                <Box component="span" style={{ fontWeight: 700, fontSize: '15px' }}>
                                  {i18next.t('common:RouteList.tour')}
                                </Box>{' '}
                                <Box component="span" style={{ fontWeight: 400, fontSize: '13px' }}>
                                  {`${route.id}-0`}
                                </Box>
                              </Typography>
                            </Grid>

                            <Grid container direction="column" spacing={1} style={{ padding: '0px 4px' }}>
                              <Grid item>
                                <Grid container alignItems="center" spacing={1}>
                                  <Grid item>
                                    <StraightenOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                  </Grid>
                                  <Grid className={classes.detailSec}>
                                    <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                      -- km
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Grid container alignItems="center" spacing={0.5}>
                                  <Grid item>
                                    <AccessTimeOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                  </Grid>
                                  <Grid className={classes.detailSec}>
                                    <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                      --
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Grid container alignItems="center" spacing={1}>
                                  <Grid item>
                                    <RoomOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                  </Grid>
                                  <Grid className={classes.detailSec}>
                                    <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                      -- {i18next.t('common:VehicleRouteList.stop')}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Grid container alignItems="center" spacing={1}>
                                  <Grid item>
                                    <ViewInArOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                  </Grid>
                                  <Grid className={classes.detailSec}>
                                    <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                      {pendingForVehicle} {i18next.t('common:VehicleRouteList.shipments')}{' '}
                                      <Typography component="span" variant="body2" sx={{ ml: 0.75, fontWeight: 600, color: 'success.main' }}>
                                        (+{pendingForVehicle})
                                      </Typography>
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Grid container alignItems="center" spacing={1}>
                                  <Grid item>
                                    <LocalShippingOutlined className={classes.TourMapDetail} style={{ color: '#a7a7a7' }} />
                                  </Grid>
                                  <Grid className={classes.detailSec}>
                                    <Typography variant="body2" className={classes.detailinfo} style={{ lineHeight: 1 }}>
                                      {load_class ?? '--'}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Box>
                        ) : (
                          /* ===== Still truly empty ===== */
                          <Typography variant="body2" sx={{ p: 1 }}>
                            {i18next.t('common:DroppedShipment.EmptyTextMessage')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Card>
                </ListItem>
              )}
            </DroppableVehicle>
          );
        })}
      </List>
    </Card>
  );
};

export default RoutesList;
